
import { Vue, Options } from "vue-class-component";
import formatDateWithoutTime from "@/app/infrastructures/misc/common-library/FormatDateWithoutTime";
import {
  HubInOutManifest,
  HubInOutManifestRequest,
  HubManifestDetail,
  HubInOutManifestExcelRequest
} from "@/domain/entities/HubToHub";
import { HubToHubController } from "@/app/ui/controllers/HubToHubController";
import { getHubData } from "@/app/infrastructures/misc/Cookies";
import { AccountController } from "@/app/ui/controllers/AccountController";
import {
  formatDateNormal,
  formatByCountry
} from "@/app/infrastructures/misc/Utils";
import debounce from "lodash/debounce";
import { MainAppController } from "@/app/ui/controllers/MainAppController";

@Options({})
export default class OutHub extends Vue {
  detailHubManifest = new HubManifestDetail();

  beforeMount() {
    this.clearFilter();
  }

  clearFilter() {
    HubToHubController.setSearch("");
    HubToHubController.setFirstPage();
    HubToHubController.setStartDate("");
    HubToHubController.setEndDate("");
  }

  onSearch = debounce((value: string) => {
    HubToHubController.setSearch(value);
    HubToHubController.setFirstPage();
    this.getListOutHub();
  }, 500);

  clearSearch() {
    HubToHubController.setSearch("");
    HubToHubController.setFirstPage();
    this.getListOutHub();
  }

  get isLoading() {
    return HubToHubController.isLoadingOutHubList;
  }

  get startDate() {
    return HubToHubController.startDate;
  }

  get endDate() {
    return HubToHubController.endDate;
  }

  get search() {
    return HubToHubController.search;
  }

  get offset() {
    return this.pagination.limit * (this.pagination.page - 1);
  }

  setDateRange(value: Array<any>) {
    HubToHubController.setStartDate(value[0]);
    HubToHubController.setEndDate(value[1]);
    value[0] && value[1] && HubToHubController.setFirstPage();
    value[0] && value[1] && this.getListOutHub();
  }

  getListOutHub() {
    const params = new HubInOutManifestRequest({
      hmNo: this.search,
      startDate: new Date(this.startDate).toLocaleDateString("fr-CA"),
      endDate: new Date(this.endDate).toLocaleDateString("fr-CA"),
      createdType: "",
      accountId: 0,
      page: this.pagination.page,
      limit: this.pagination.limit,
      hmHubId: getHubData()?.hubId || 0
    });
    HubToHubController.getHubOutManifestList(params);
  }

  get firstColumn() {
    return [
      {
        name: this.$t("hubToHub.no"),
        styleHead: "w-10 text-left",
        render: (_item: HubInOutManifest, index: any) => {
          return `<div class="overflow-ellipsis text-left">
            ${this.offset + index + 1}</div>`;
        }
      },
      {
        name: this.$t("hubToHub.noManifest"),
        styleHead: "w-44 text-left",
        render: (item: HubInOutManifest) => {
          return `<div class="overflow-ellipsis text-left">
            ${item.hmNo}</div>`;
        }
      },
      {
        name: this.$t("hubToHub.totalStt"),
        styleHead: "w-32 text-left",
        render: (item: HubInOutManifest) => {
          return `<div class="overflow-ellipsis text-left" data-testid="hub-to-hub-list-cell-total-stt">
            ${formatByCountry(item.hmStts, { type: "quantity" })}</div>`;
        }
      }
    ];
  }
  get secondColumn() {
    return [
      {
        name: this.$t("hubToHub.totalKoli"),
        styleHead: "w-32 text-left",
        render: (item: HubInOutManifest) => {
          return `<div class="overflow-ellipsis text-left" data-testid="hub-to-hub-list-cell-total-koli">
            ${formatByCountry(item.hmSttPieces, { type: "quantity" })}</div>`;
        }
      },
      {
        name: this.$t("hubToHub.totalGrossWeight"),
        styleHead: "w-44 text-left",
        render: (item: HubInOutManifest) => {
          return `<div class="overflow-ellipsis text-left" data-testid="hub-to-hub-list-cell-gross-weight">
            ${formatByCountry(item.hmSttGrossWeights, {
              type: "weight"
            })}</div>`;
        }
      },
      {
        name: this.$t("hubToHub.manifestDate"),
        styleHead: "w-64 text-left",
        render: (item: HubInOutManifest) => {
          return `<div class="flex flex-col"><div class="overflow-ellipsis text-left">${formatDateWithoutTime(
            item.hmCreatedAt
          )}<div class="overflow-ellipsis text-gray-lp-600">${
            item.hmCreatedByName
          }</div></div>`;
        }
      },
      {
        name: this.$t("hubToHub.plateNumber"),
        styleHead: "w-44 text-left",
        render: (item: HubInOutManifest) => {
          return `<div class="overflow-ellipsis text-left">
            ${item.hmPlateNumber}</div>`;
        }
      }
    ];
  }
  get columns() {
    return [
      ...this.firstColumn,
      ...this.secondColumn,
      {
        name: this.$t("hubToHub.hubDestination"),
        styleHead: "w-44 text-left",
        render: (item: HubInOutManifest) => {
          return `<div class="overflow-ellipsis text-left">
            ${item.hmHubDestinationName}</div>`;
        }
      },
      {
        name: this.$t("hubToHub.actions"),
        key: "button_column",
        styleHead: "w-24 text-left",
        styleCustom: "padding-action-sti",
        styleButton: (item: HubInOutManifest) => {
          return {
            outline: true,
            icon: "option-gray",
            title: "",
            textColor: "black",
            color: "gray-lp-400",
            clickFunction: () => null,
            groupOptions: [
              {
                icon: "print-summary",
                label: this.$t("hubToHub.printSummary"),
                dataTestid: "hub-to-hub-action-print-summary",
                clickFunction: () =>
                  this.print("summary", Number(item.hmId), item.hmNo)
              },
              {
                icon: "printer",
                label: this.$t("hubToHub.printPdf"),
                dataTestid: "hub-to-hub-action-print-manifest",
                clickFunction: () =>
                  this.print("manifest", Number(item.hmId), item.hmNo)
              },
              {
                icon: "document-download-outline-grey",
                label: this.$t("hubToHub.downloadExcel"),
                dataTestid: "hub-to-hub-action-download-excel",
                clickFunction: () => this.getDataExcel(Number(item.hmId), this.$t('hubToHub.manifestOuthub'))
              }
            ]
          };
        }
      }
    ];
  }

  get outHubManifestList() {
    return HubToHubController.OutHubDataManifestList.data;
  }
  get pagination() {
    return HubToHubController.OutHubDataManifestList.pagination;
  }
  get dataProfile() {
    return AccountController.accountData;
  }
  get roleAccount() {
    return (
      (this.dataProfile.account_type === "partner"
        ? this.dataProfile.account_type_detail.type
        : this.dataProfile.account_type) || "internal"
    );
  }

  async print(type: string, hmId: number, hmNo: string) {
    MainAppController.showLoading();
    this.detailHubManifest = await HubToHubController.getHubManifestDetail(
      hmId
    );
    const data: any = this.detailHubManifest ? [
      {
        name: `${this.$t("hubToHub.hubOrigin")} - ${this.$t("hubToHub.hubDestination")}`,
        value:
          `${this.detailHubManifest.hmHubName} - ${
            this.detailHubManifest.hmHubDestinationName
          }` || "-"
      },
      {
        name: this.$t("hubToHub.consolidatorLocation"), 
        value: `${this.dataProfile.account_location.city_code} - ${this.dataProfile.account_location.city_name}`
      },
      {
        name: this.$t("hubToHub.totalSttKoli"),
        value:
          `${formatByCountry(this.detailHubManifest.hmStts, { type: "quantity" })}/${formatByCountry(this.detailHubManifest.hmSttPieces, { type: "quantity" })}` ||
          "-"
      },
      {
        name: this.$t("hubToHub.totalWeight"),
        value: formatByCountry(this.detailHubManifest.hmSttGrossWeights, { type: "weight", suffix: " kg" }) || "-"
      },
      {
        name: this.$t("hubToHub.manifestDate"),
        value:
          formatDateNormal(
            this.detailHubManifest.hmCreatedAt,
            "DD MMMM YYYY, HH:mm"
          )
      },
      {
        name: this.$t("hubToHub.createdBy"),
        value: this.detailHubManifest.hmCreatedByName || "-"
      }
    ] : [];

    const print: any = await import(
      "@/app/ui/views/hub-to-hub/modules/print.vue"
    );
    const logo = await import(
      `@/app/ui/assets/images/logo-${this.roleAccount}.png`
    );

    const printType: any = {
      summary: () =>
        print.default.methods.printSummaryOutHub(data, hmNo, logo.default),

      manifest: () =>
        print.default.methods.printManifestOutHub(
          hmNo,
          this.detailHubManifest,
          logo.default,
          true
        )
    };
    MainAppController.closeLoading();

    printType[type]();
  }
  async getDataExcel(id: number, type: string) {
    const params = new HubInOutManifestExcelRequest({
      id: id,
      type: type
    });
    await HubToHubController.getHubManifestExcel(params);
  }
}
